import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { find } from 'lodash';
import {
    Datagrid,
    DateField,
    FunctionField,
    List,
    TextField,
    TextInput,
    useNotify,
    usePermissions,
    useTranslate,
    useUpdate,
} from 'react-admin';
import { Outlet, useParams } from 'react-router';
import { ROLE_DELETE_USER, USER_PROFILES, USERS } from '@/utils/CONST';
import CustomBlock from '@/components/common/custom-block/custom-block';
import SwitchComponent from '@/components/common/custom-fields/switch-component';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import UserActionField from '@/components/agencies/users/user-action-field';
import UsersListActions from '@/components/agencies/users/users-list-actions';

const AgencyUsersList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const [update] = useUpdate();
    const notify = useNotify();
    const { agencyId } = useParams();

    const handleEnableUser = ({ target: { checked }, id }: any) => {
        update(
            USERS,
            {
                id,
                data: {
                    enabled: checked,
                },
            },
            {
                mutationMode: 'optimistic',
                onSuccess: () => {
                    notify(checked ? 'messages.user.activated' : 'messages.user.deactivated', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                },
                onError: () => {
                    notify('messages.error', { type: 'error' });
                },
            },
        );
    };

    return (
        <CustomBlock label={translate('main.navigation.users')}>
            <ListStylesContainer>
                <List
                    actions={<UsersListActions />}
                    component='div'
                    resource={USERS}
                    filter={{
                        groups: agencyId,
                        hidden: false,
                        isSemiAnonymous: false,
                    }}
                    sort={{
                        order: 'DESC',
                        field: 'createdAt',
                    }}
                    empty={false}
                    storeKey='managerUsersList'
                    filters={[
                        <TextInput
                            source='lastName'
                            label={'main.navigation.last_name'}
                            variant='standard'
                            resettable
                        />,
                        <TextInput source='firstName' label={'main.navigation.name'} variant='standard' resettable />,
                        <TextInput source='email' label='main.navigation.email' variant='standard' resettable />,
                    ]}
                    disableSyncWithLocation
                    queryOptions={{ enabled: !!agencyId }}
                >
                    <Datagrid bulkActionButtons={false}>
                        <TextField label={'main.navigation.last_name'} source='lastName' />

                        <TextField source='firstName' label={'main.navigation.name'} />

                        <TextField label={'main.navigation.email'} source='email' />

                        {/*<BooleanField label={'users.list.enabled'} source={'enabled'} />*/}

                        <DateField
                            label={'main.navigation.last_login'}
                            options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                            source={'lastLogin'}
                        />

                        <FunctionField
                            label={'users.list.user_type'}
                            render={({ tags }: any) => {
                                const aux = find(tags, (tag: string) =>
                                    USER_PROFILES.includes(tag.toUpperCase()),
                                )?.toLowerCase();
                                return (
                                    <Typography sx={{ color: !aux ? 'red' : 'black' }}>
                                        {aux
                                            ? translate(`users.profiles.${aux}`)
                                            : 'BIG_CLIENT_SITE_OPERATOR_CREATE_SITE'}
                                    </Typography>
                                );
                            }}
                        />

                        <FunctionField
                            label={'users.list.enabled'}
                            render={({ enabled, id }: { enabled: boolean; id: string }) => (
                                <SwitchComponent
                                    data-cy='activate-switch'
                                    checked={!!enabled}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleEnableUser({ target: { checked: e.target.checked }, id });
                                    }}
                                />
                            )}
                        />

                        {permissions?.includes(ROLE_DELETE_USER) && (
                            <Box display={'flex'} alignItems={'center'} gap={2}>
                                <FunctionField render={({ id }: any) => <UserActionField userId={id} />} />
                            </Box>
                        )}
                    </Datagrid>
                </List>
            </ListStylesContainer>

            <Outlet />
        </CustomBlock>
    );
};

export default AgencyUsersList;
